/**
 * Maps installed third-party application bundle IDs to NVD CPE vendor/product
 * tuples and runs CVE lookups against the NVD v2 API.
 *
 * Matching is best-effort: NVD's CPE dictionary covers most popular desktop apps
 * but not every internal/enterprise app. Unknown apps fall back to a keyword
 * search using a sanitized product name.
 */
import { NvdCveData, searchCvesByCpe, searchCvesByKeyword } from "./nvdApi";

export interface CveMatchResult {
  bundleId: string;
  version: string;
  matched: boolean;
  source: "cpe" | "keyword" | "none";
  cves: NvdCveData[];
  highestSeverity: NvdCveData["severity"];
}

/**
 * Aggregated CVE match result across all detected versions of a single app.
 * Used by the UI so the catalog row reflects every installed version.
 */
export interface AggregatedAppCveResult {
  bundleId: string;
  /** Versions that were actually evaluated against NVD. */
  versionsEvaluated: string[];
  /** Versions that produced at least one CVE match. */
  vulnerableVersions: string[];
  /** Deduped CVE list across every evaluated version (highest severity wins on dupes). */
  cves: NvdCveData[];
  highestSeverity: NvdCveData["severity"];
  /** Per-version breakdown for drill-down. */
  perVersion: CveMatchResult[];
  /** True when at least one version produced any kind of match (even if 0 CVEs). */
  matched: boolean;
}


interface VendorProduct {
  vendor: string;
  product: string;
}

// Curated bundle id -> NVD vendor/product mapping for popular third-party apps.
// Add entries as the fleet grows. Keys are lowercase bundle IDs.
const BUNDLE_ID_MAP: Record<string, VendorProduct> = {
  "com.google.chrome": { vendor: "google", product: "chrome" },
  "com.google.chrome.canary": { vendor: "google", product: "chrome" },
  "org.mozilla.firefox": { vendor: "mozilla", product: "firefox" },
  "org.mozilla.firefoxdeveloperedition": { vendor: "mozilla", product: "firefox" },
  "com.microsoft.edgemac": { vendor: "microsoft", product: "edge_chromium" },
  "com.microsoft.vscode": { vendor: "microsoft", product: "visual_studio_code" },
  "com.microsoft.teams": { vendor: "microsoft", product: "teams" },
  "com.microsoft.teams2": { vendor: "microsoft", product: "teams" },
  "com.microsoft.outlook": { vendor: "microsoft", product: "outlook" },
  "com.microsoft.word": { vendor: "microsoft", product: "word" },
  "com.microsoft.excel": { vendor: "microsoft", product: "excel" },
  "com.microsoft.powerpoint": { vendor: "microsoft", product: "powerpoint" },
  "com.tinyspeck.slackmacgap": { vendor: "slack", product: "slack" },
  "us.zoom.xos": { vendor: "zoom", product: "meetings" },
  "com.brave.browser": { vendor: "brave", product: "brave" },
  "com.operasoftware.opera": { vendor: "opera", product: "opera" },
  "com.vivaldi.vivaldi": { vendor: "vivaldi", product: "vivaldi" },
  "com.agilebits.onepassword7": { vendor: "1password", product: "1password" },
  "com.1password.1password": { vendor: "1password", product: "1password" },
  "com.docker.docker": { vendor: "docker", product: "desktop" },
  "com.spotify.client": { vendor: "spotify", product: "spotify" },
  "com.notion.id": { vendor: "notion", product: "notion" },
  "notion.id": { vendor: "notion", product: "notion" },
  "com.adobe.reader": { vendor: "adobe", product: "acrobat_reader" },
  "com.adobe.acrobat.pro": { vendor: "adobe", product: "acrobat_pro" },
  "com.adobe.photoshop": { vendor: "adobe", product: "photoshop" },
  "com.tdesktop.telegram": { vendor: "telegram", product: "telegram_desktop" },
  "com.whatsapp.whatsappdesktop": { vendor: "whatsapp", product: "whatsapp" },
  "com.jetbrains.intellij": { vendor: "jetbrains", product: "intellij_idea" },
  "com.jetbrains.pycharm": { vendor: "jetbrains", product: "pycharm" },
  "com.jetbrains.webstorm": { vendor: "jetbrains", product: "webstorm" },
  "com.github.atom": { vendor: "github", product: "atom" },
  "com.sublimetext.4": { vendor: "sublimehq", product: "sublime_text" },
  "com.sublimetext.3": { vendor: "sublimehq", product: "sublime_text" },
  "com.cisco.webexmeetingsapp": { vendor: "cisco", product: "webex_meetings" },
  "com.citrix.receiver.helper": { vendor: "citrix", product: "workspace" },
  "org.videolan.vlc": { vendor: "videolan", product: "vlc_media_player" },
  "com.git-tower.tower3": { vendor: "fournova", product: "tower" },
  "com.dropbox.dropbox": { vendor: "dropbox", product: "dropbox" },
  "com.box.desktop": { vendor: "box", product: "drive" },
  "com.skype.skype": { vendor: "microsoft", product: "skype" },
  "com.atlassian.confluence": { vendor: "atlassian", product: "confluence" },
  "com.atlassian.jira": { vendor: "atlassian", product: "jira" },
};

export function isAppleApp(bundleId: string): boolean {
  if (!bundleId) return false;
  const id = bundleId.toLowerCase();
  return id.startsWith("com.apple.") || id === "apple";
}

export function isMatchableApp(bundleId: string, version: string | null | undefined): boolean {
  if (!bundleId || isAppleApp(bundleId)) return false;
  if (!version) return false;
  const v = version.trim().toLowerCase();
  if (!v || v === "unknown" || v === "n/a") return false;
  return true;
}

export function lookupVendorProduct(bundleId: string): VendorProduct | null {
  return BUNDLE_ID_MAP[bundleId.toLowerCase()] ?? null;
}

/**
 * Build a CPE 2.3 match string for the given vendor/product/version.
 * Version may include build numbers; we keep it as-is and rely on virtualMatchString.
 */
export function buildCpe(vendor: string, product: string, version: string): string {
  const safeVersion = version.replace(/[^a-zA-Z0-9._-]/g, "_");
  return `cpe:2.3:a:${vendor}:${product}:${safeVersion}:*:*:*:*:*:*:*`;
}

/**
 * Derive a fallback keyword search term from bundle ID + display name.
 */
export function inferKeyword(bundleId: string, displayName?: string): string {
  if (displayName && displayName.trim().length >= 3) return displayName.trim();
  const parts = bundleId.split(".");
  return (parts[parts.length - 1] || bundleId).replace(/[-_]/g, " ");
}

const SEVERITY_RANK: Record<NvdCveData["severity"], number> = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
  NONE: 0,
};

export function highestSeverity(cves: NvdCveData[]): NvdCveData["severity"] {
  let best: NvdCveData["severity"] = "NONE";
  for (const c of cves) {
    if (SEVERITY_RANK[c.severity] > SEVERITY_RANK[best]) best = c.severity;
  }
  return best;
}

/**
 * Look up CVEs for a single installed app version. Tries CPE first
 * when we have a known vendor/product mapping, then falls back to keyword search.
 */
export async function matchAppCves(
  bundleId: string,
  version: string,
  displayName?: string,
): Promise<CveMatchResult> {
  const empty: CveMatchResult = {
    bundleId,
    version,
    matched: false,
    source: "none",
    cves: [],
    highestSeverity: "NONE",
  };

  if (!isMatchableApp(bundleId, version)) return empty;

  const vp = lookupVendorProduct(bundleId);
  if (vp) {
    const cpe = buildCpe(vp.vendor, vp.product, version);
    const cves = await searchCvesByCpe(cpe, 25);
    if (cves.length > 0) {
      return {
        bundleId,
        version,
        matched: true,
        source: "cpe",
        cves,
        highestSeverity: highestSeverity(cves),
      };
    }
    // Try product-only match (no version) — useful when version isn't in NVD CPE dict
    const broaderCpe = buildCpe(vp.vendor, vp.product, "*");
    const broader = await searchCvesByCpe(broaderCpe, 25);
    if (broader.length > 0) {
      return {
        bundleId,
        version,
        matched: true,
        source: "cpe",
        cves: broader,
        highestSeverity: highestSeverity(broader),
      };
    }
  }

  // Fallback: keyword search
  const keyword = inferKeyword(bundleId, displayName);
  if (keyword.length < 3) return empty;
  const cves = await searchCvesByKeyword(keyword, 15);
  if (cves.length === 0) return empty;
  return {
    bundleId,
    version,
    matched: true,
    source: "keyword",
    cves,
    highestSeverity: highestSeverity(cves),
  };
}

/**
 * Aggregate per-version CveMatchResults for a single app into one combined result,
 * deduplicating CVEs by ID (keeping the highest severity instance) and tracking
 * which versions are actually vulnerable.
 */
export function aggregateAppCveResults(
  bundleId: string,
  perVersion: CveMatchResult[],
): AggregatedAppCveResult {
  const dedup = new Map<string, NvdCveData>();
  const vulnerableSet = new Set<string>();
  const vulnerable: string[] = [];
  const evaluatedSet = new Set<string>();
  const evaluated: string[] = [];

  for (const r of perVersion) {
    const version = (r.version ?? "").trim();
    if (version && !evaluatedSet.has(version)) {
      evaluatedSet.add(version);
      evaluated.push(version);
    }
    // Only count a version as vulnerable when it actually produced CVEs.
    if (version && r.cves.length > 0 && !vulnerableSet.has(version)) {
      vulnerableSet.add(version);
      vulnerable.push(version);
    }
    for (const cve of r.cves) {
      const existing = dedup.get(cve.cveId);
      if (!existing || SEVERITY_RANK[cve.severity] > SEVERITY_RANK[existing.severity]) {
        dedup.set(cve.cveId, cve);
      }
    }
  }

  // Final guard: drop any version from vulnerableVersions whose CVEs were all
  // deduped away (shouldn't happen, but keeps the invariant explicit).
  const survivingCveIds = new Set(dedup.keys());
  const vulnerableFinal = vulnerable.filter((v) =>
    perVersion.some(
      (r) => (r.version ?? "").trim() === v && r.cves.some((c) => survivingCveIds.has(c.cveId)),
    ),
  );

  const cves = Array.from(dedup.values());
  return {
    bundleId,
    versionsEvaluated: evaluated,
    vulnerableVersions: vulnerableFinal,
    cves,
    highestSeverity: highestSeverity(cves),
    perVersion,
    matched: perVersion.some((r) => r.matched),
  };
}

